/**
 * Route query + session helpers for the group-mode ticket-group multiselect.
 * Empty selection means all groups (same convention as accounts).
 */
class TicketGroupScopeUtility {
  /**
   * @param {import('vue-router').LocationQuery} query
   * @returns {string[]}
   */
  parseIds(query) {
    const raw = query?.ticketGroup
    if (raw == null || raw === '') return []
    return (Array.isArray(raw) ? raw : [raw]).map(String).filter(Boolean)
  }

  /**
   * @param {string[]} ids
   * @returns {string|string[]|null}
   */
  toQueryValue(ids) {
    if (!ids?.length) return null
    return ids.length === 1 ? ids[0] : [...ids]
  }

  /**
   * Union of account ids for the selected ticket groups (session snapshot).
   *
   * @param {string[]} groupIds
   * @param {Array<{ _id: string, accounts?: Array<string|{ _id: string }> }>} [ticketGroups]
   * @param {string[]|null} [activeAccountIds] When set, keep only active allowed ids.
   * @returns {string[]}
   */
  accountIds(groupIds, ticketGroups = [], activeAccountIds = null) {
    if (!groupIds?.length) return []
    const wanted = new Set(groupIds.map(String))
    const out = new Set()
    for (const group of ticketGroups) {
      if (!wanted.has(String(group._id))) continue
      for (const acc of group.accounts ?? []) {
        const id = acc && typeof acc === 'object' ? acc._id : acc
        if (id) out.add(String(id))
      }
    }
    if (activeAccountIds == null) return [...out]
    const active = new Set(activeAccountIds.map(String))
    return [...out].filter((id) => active.has(id))
  }

  /**
   * @param {string[]} a
   * @param {string[]} b
   * @returns {boolean}
   */
  sameAccountIds(a, b) {
    const left = [...new Set((a ?? []).map(String))].sort()
    const right = [...new Set((b ?? []).map(String))].sort()
    return left.length === right.length && left.every((id, i) => id === right[i])
  }

  /**
   * True when account multiselect overrides ticket-group route mode — empty pick
   * (kaikki), any id outside the groups' account union, or a proper subset of it.
   *
   * @param {string[]} pickedAccountIds
   * @param {string[]} ticketGroupIds
   * @param {Array<{ _id: string, accounts?: Array<string|{ _id: string }> }>} [ticketGroups]
   * @returns {boolean}
   */
  accountsBreakTicketGroupSelection(pickedAccountIds, ticketGroupIds, ticketGroups = [], activeAccountIds = null) {
    if (!ticketGroupIds?.length) return false
    if (!pickedAccountIds?.length) return true
    const allowed = this.accountIds(ticketGroupIds, ticketGroups, activeAccountIds)
    return !this.sameAccountIds(pickedAccountIds, allowed)
  }

  /**
   * @param {Record<string, unknown>} query
   * @param {string[]} groupIds
   * @returns {Record<string, unknown>}
   */
  withQuery(query, groupIds) {
    const next = { ...query }
    const value = this.toQueryValue(groupIds)
    if (value == null) delete next.ticketGroup
    else next.ticketGroup = value
    return next
  }

  /**
   * Drop route ids that are missing from the session snapshot (deleted / no access).
   *
   * @param {string[]} groupIds
   * @param {Array<{ _id: string }>} [ticketGroups]
   * @returns {string[]}
   */
  sanitizeIds(groupIds, ticketGroups = []) {
    if (!groupIds?.length) return []
    const known = new Set((ticketGroups ?? []).map((group) => String(group._id)))
    return groupIds.map(String).filter((id) => known.has(id))
  }

  /**
   * @param {string[]} a
   * @param {string[]} b
   * @returns {boolean}
   */
  sameGroupIds(a, b) {
    const left = [...new Set((a ?? []).map(String))].sort()
    const right = [...new Set((b ?? []).map(String))].sort()
    return left.length === right.length && left.every((id, i) => id === right[i])
  }
}

export default new TicketGroupScopeUtility()
